import {
  DEFAULT_PRIMARY_SECTOR,
  type PrimarySector,
} from "./sectors";

interface Category {
  title: string;
  description: string;
  imageURL?: string;
}

/**
 * The original taxonomy. Existing `company_profile` and `investor_profile`
 * rows store these titles and their matching `secondary_sector` slugs, so
 * nothing here may be renamed or removed.
 */
const healthCategories: Array<Category> = [
  {
    title: "Healthcare-IT",
    description:
      "According to recent data, healthcare-IT is tipped to become a $390 billion industry by 2024. From health information exchanges (HIEs), through to virtual consultations, our entrepreneurs focus on solving some of the sector's greatest challenges by providing solutions that support healthcare professionals to make more insightful decisions through enhanced data collection, interpretation and communication.",
    imageURL: "/category-it.png",
  },
  {
    title: "Big Data Analytics",
    description:
      "Big data analytics has changed the way we understand many industries around the world and healthcare is no exception. Through the careful management, understanding and processing of certain data points, the healthcare industry is not only able to form more accurate models that may prevent future pandemics, but also reduce a broad range of operational and production costs, while saving lives.",
    imageURL: "/category-bigdata.png",
  },
  {
    title: "Augmented /<br/> Virtual Reality",
    description:
      "Thanks in part to the power of enhanced communication technologies such as 5G, AR and VR have a considerable role to play in the future of healthcare, ranging from superficial marketing apps such as 'virtually' trying on new glasses, through to remote training, patient treatment and disease awareness. Estimated to be valued at $2.4 billion by 2026, the AR/VR industry is likely to be one of the most high-impact technology sectors in the coming years.",
    imageURL: "/category-vr.png",
  },
  {
    title: "Patient Monitoring &<br/>Digital Therapeutics",
    description:
      "Through the wider use of Internet of Things (IoT) technology, patient monitoring has been able to go beyond just observing physiological measurements such as heart rate and blood pressure and into a new era that seeks to understand behavioural traits such as medication compliance and allowing for monitoring in the home and workplace, significantly transforming delivery of healthcare. Advances in digital health will soon see digital therapeutics become a core part of medicine in conjunction with hardware and drug-based treatments.",
    imageURL: "/category-therapeutics.png",
  },
  {
    title: "Diagnostics & Imaging",
    description:
      "As a sector which is in the middle of a transition from 2D to 3D, the diagnostics and imaging market will benefit from various developments including AI-driven analytics to improve accuracy and speed of decision making, augmented intelligence to improve repetitive manual processes and nuclear imaging to support in the diagnosis of conditions such as thyroid diseases, heart conditions and cancer.",
    imageURL: "/category-imaging.png",
  },
  {
    title: "Medical Devices<br/> / Wearables",
    description:
      "While several major multinational corporations have already entered the medical devices/ wearables market, recent statistics show that in 2019 it was worth $456.9 billion and projected to reach $603 billion in 2023, with a greater emphasis on apparel integration, a wider array of sensors and the development of the implants market, which will soon become integrated with remote monitoring technology, while adhering to strict privacy standards.",
    imageURL: "/category-wearables.png",
  },
  {
    title: "3D Printing",
    description:
      "3D printing has already shown to be an enormous opportunity for the medical industry estimated worth $1.3 billion growing to $6 billion by 2027, with its key applications including medical/ surgical devices or instruments. In addition, 3D bioprinting has already been used to create living human cells and tissue in regenerative medicine and tissue engineering, while in the world of medicine it helps manufacture precision and personalized pharmaceutics.",
    imageURL: "/category-3dprinting.png",
  },
  {
    title: "Genomics, Precision<br /> Medicine & Techbio",
    description:
      "Thanks to the significantly reduced cost of DNA sequencing through to a greater understanding of genetic diseases, genomics and tailored precision medicine will soon include the functional knowledge of each gene's role, how our environment affects our gene expression, the widespread integration of genome testing and the eradication of commonplace diseases.",
    imageURL: "/category-genomics.png",
  },
  {
    title: "Artificial Intelligence <br/>& Machine Learning",
    description:
      "Having already attracted a significant volume of investment, the potential benefits of AI in healthcare are enormous, ranging from improving drug discovery, helping to predict the early diagnosis of diseases such as Alzheimer's through to absorbing patient data and using it to produce useful algorithms that may help to provide life-saving insights while reducing the need for costly tests and procedures.",
    imageURL: "/category-ai.png",
  },
  {
    title: "Sensors, Robotics<br/> & Prosthetics",
    description:
      "As a field that has harnessed the imagination of mankind for almost a century, the evolution of sensors and robotics, playing an increasing role in our medical world is a fascinating one, ranging from assessments and logistics, through to a new frontier of prosthetics that interface brains with computers to change the way those with either physical or neurological damage are able to operate and function.",
    imageURL: "/category-prosthetics.png",
  },
  {
    title: "Telehealth / mHealth",
    description:
      "Spurred on by the recent pandemic, Telehealth has seen a massive period of growth, with one source claiming that telehealth news mentions tripled between February and April 2021. Subsequently, the industry's investment market is on track to exceed 2020's investments, which achieved $6.6bn across 490 deals in the US. As of May 2021, $2.6bn has already been raised across 194 deals.",
    imageURL: "/category-telehealth.png",
  },
  {
    title: "Tools & Platforms",
    description:
      "Since the advent of behavioural phenotyping, the development of new tools and platforms that are tailored around individuals offer a massive upside in understanding the full scope of the patient, meaning a wider array of factors can both be learned and understood, thereby helping to make both long and short term treatments more effective.",
    imageURL: "/category-platforms.png",
  },
];

/**
 * Climate taxonomy. Titles and descriptions are Milla's copy verbatim; do not
 * reword them without a fresh instruction from her.
 *
 * The artwork referenced by `imageURL` lives in `public/`, cover-cropped to
 * 513x420 to match the health artwork the same grid renders. The supplied
 * originals were 2 MB to 16 MB JPEGs, so they are resized before committing.
 * A missing background image only shows up as a flat grey tile, never an
 * error, so `categories-artwork.test.mjs` asserts each file is on disk.
 */
const climateCategories: Array<Category> = [
  {
    title: "Advanced Materials",
    description:
      "Advanced materials are enabling the next generation of sustainable products and infrastructure by improving performance, durability and resource efficiency. From novel composites and nanomaterials to smart coatings and engineered polymers, innovations in this field support cleaner manufacturing, energy systems and industrial applications while reducing environmental impact.",
    imageURL: "/category-climate-advanced-materials.jpg",
  },
  {
    title: "AI, Digital & Software",
    description:
      "Artificial intelligence, digital technologies and software are transforming the climate and energy sectors by improving decision-making, automation and operational efficiency. From predictive analytics and digital twins to optimisation platforms and climate intelligence, these solutions help organisations reduce emissions, manage resources more effectively and accelerate the transition to a low-carbon economy.",
    imageURL: "/category-climate-ai-digital-software.jpg",
  },
  {
    title: "Batteries & Energy Storage",
    description:
      "Energy storage technologies are essential for integrating renewable energy and improving the resilience of modern power systems. From advanced lithium-ion batteries and solid-state technologies to long-duration storage and thermal solutions, innovators in this sector are helping balance electricity supply and demand while supporting electrification across multiple industries.",
    imageURL: "/category-climate-batteries-energy-storage.jpg",
  },
  {
    title: "Bioenergy & Sustainable Fuels",
    description:
      "Bioenergy and sustainable fuels provide lower-carbon alternatives to conventional fossil fuels across transport, industry and power generation. By converting biological resources, waste streams and renewable feedstocks into usable energy, these technologies help reduce greenhouse gas emissions while supporting the transition to more sustainable energy systems.",
    imageURL: "/category-climate-bioenergy-sustainable-fuels.jpg",
  },
  {
    title: "Carbon Capture, Utilisation & Storage (CCUS)",
    description:
      "Carbon capture, utilisation and storage technologies are designed to prevent carbon dioxide emissions from entering the atmosphere. By capturing emissions from industrial processes or power generation and either storing or repurposing them, CCUS plays an important role in reducing emissions from sectors where decarbonisation remains challenging.",
    imageURL: "/category-climate-ccus.jpg",
  },
  {
    title: "Carbon Removal & Carbon Management",
    description:
      "Carbon removal and management technologies focus on reducing atmospheric carbon dioxide while helping organisations measure, monitor and manage emissions. From direct air capture and nature-based solutions to carbon accounting and monitoring platforms, these innovations support global net-zero ambitions and informed climate decision-making.",
    imageURL: "/category-climate-carbon-removal.jpg",
  },
  {
    title: "Circular Economy & Resource Efficiency",
    description:
      "The circular economy aims to maximise the value of resources by reducing waste, extending product lifecycles and promoting reuse. Companies in this sector develop technologies and business models that improve material efficiency, reduce environmental impact and support more sustainable patterns of production and consumption.",
    imageURL: "/category-climate-circular-economy.jpg",
  },
  {
    title: "Clean Manufacturing & Industrial Decarbonisation",
    description:
      "Industrial activity accounts for a significant proportion of global emissions, creating demand for cleaner production methods. This sector includes technologies that improve manufacturing efficiency, electrify industrial processes, reduce emissions and enable industries to transition towards more sustainable and competitive operations.",
    imageURL: "/category-climate-clean-manufacturing.jpg",
  },
  {
    title: "Energy Efficiency & Demand Management",
    description:
      "Improving energy efficiency is one of the fastest and most cost-effective ways to reduce emissions. Technologies in this sector optimise energy consumption across buildings, industry and infrastructure, while demand management solutions help balance electricity usage and improve overall system performance.",
    imageURL: "/category-climate-energy-efficiency.jpg",
  },
  {
    title: "Environmental Monitoring & Sensors",
    description:
      "Accurate environmental data is essential for managing natural resources and responding to climate change. From air and water quality monitoring to satellite sensing and industrial emissions tracking, these technologies provide the insights needed to improve compliance, inform policy and support sustainable decision-making.",
    imageURL: "/category-climate-environmental-monitoring.jpg",
  },
  {
    title: "Green Buildings & Construction",
    description:
      "The built environment plays a major role in global energy consumption and emissions. Companies in this sector develop technologies, materials and digital solutions that improve building efficiency, reduce carbon footprints and create healthier, more sustainable places to live and work.",
    imageURL: "/category-climate-green-buildings.jpg",
  },
  {
    title: "Hydrogen & Fuel Cells",
    description:
      "Hydrogen is expected to play an important role in decarbonising industries that are difficult to electrify. From green hydrogen production and storage to fuel cell technologies, innovators are developing solutions that support cleaner transport, industrial processes and long-term energy storage.",
    imageURL: "/category-climate-hydrogen-fuel-cells.jpg",
  },
  {
    title: "Low Carbon Materials",
    description:
      "Low carbon materials reduce emissions associated with construction, manufacturing and industrial production. Innovations include sustainable cement, low-carbon steel, bio-based materials and alternative construction products that help lower embodied carbon while maintaining performance and durability.",
    imageURL: "/category-climate-low-carbon-materials.jpg",
  },
  {
    title: "Nuclear & Advanced Nuclear",
    description:
      "Nuclear energy provides reliable, low-carbon electricity that can complement renewable power generation. This sector includes advanced reactor technologies, small modular reactors (SMRs) and innovations that improve the safety, efficiency and economic viability of next-generation nuclear energy.",
    imageURL: "/category-climate-nuclear.jpg",
  },
  {
    title: "Power Systems & Smart Grids",
    description:
      "Modern electricity networks require greater flexibility to accommodate renewable generation and changing patterns of demand. Smart grids, digital infrastructure and advanced power management technologies help improve grid resilience, reliability and efficiency while enabling the transition to cleaner energy systems.",
    imageURL: "/category-climate-power-systems-smart-grids.jpg",
  },
  {
    title: "Recycling & Waste",
    description:
      "Recycling and waste management technologies are helping recover valuable materials while reducing landfill and environmental pollution. From advanced sorting systems and chemical recycling to waste processing and resource recovery, innovations in this sector support more sustainable resource management.",
    imageURL: "/category-climate-recycling-waste.jpg",
  },
  {
    title: "Renewable Energy",
    description:
      "Renewable energy technologies generate electricity from naturally replenished resources such as solar, wind, hydro, geothermal and marine energy. As costs continue to fall and deployment accelerates, renewable energy is becoming a cornerstone of the global transition towards cleaner, more sustainable power generation.",
    imageURL: "/category-climate-renewable-energy.jpg",
  },
  {
    title: "Robotics, Automation & Drones",
    description:
      "Robotics, automation and autonomous systems are improving efficiency, safety and productivity across the climate and energy sectors. Applications range from renewable energy inspection and precision agriculture to infrastructure monitoring and industrial automation, helping organisations reduce costs while improving operational performance.",
    imageURL: "/category-climate-robotics-automation-drones.jpg",
  },
  {
    title: "Sustainable Agriculture & Food Systems",
    description:
      "Agriculture is undergoing significant transformation to meet growing food demand while reducing environmental impact. Technologies in this sector improve productivity, optimise resource use and reduce emissions through innovations such as precision farming, biological solutions, controlled environment agriculture and alternative food production.",
    imageURL: "/category-climate-sustainable-agriculture.jpg",
  },
  {
    title: "Sustainable Mobility & Transport",
    description:
      "The transport sector is evolving through electrification, alternative fuels and intelligent mobility solutions. Companies developing cleaner vehicles, charging infrastructure, fleet management technologies and low-carbon transport systems are helping reduce emissions while improving the efficiency of people and goods movement.",
    imageURL: "/category-climate-sustainable-mobility.jpg",
  },
  {
    title: "Water & Wastewater",
    description:
      "Water technologies are becoming increasingly important as climate change, population growth and industrial demand place greater pressure on water resources. Innovations include water treatment, purification, monitoring, recycling and wastewater management solutions that improve efficiency, resilience and environmental sustainability.",
    imageURL: "/category-climate-water-wastewater.jpg",
  },
  {
    title: "Tools & Platforms",
    description:
      "Digital tools and technology platforms are helping organisations plan, measure and accelerate their sustainability strategies. From emissions reporting and climate risk assessment to project management and collaboration software, these solutions provide the data and insights needed to support more informed environmental decision-making.",
    imageURL: "/category-climate-tools-platforms.jpg",
  },
];

const categoriesByPrimarySector: Record<PrimarySector, Array<Category>> = {
  Health: healthCategories,
  Climate: climateCategories,
};

const getCategories = (
  primarySector: PrimarySector = DEFAULT_PRIMARY_SECTOR,
): Array<Category> => categoriesByPrimarySector[primarySector] ?? healthCategories;

/**
 * Legacy alias kept because health was the only taxonomy when this module was
 * written and several callers still import `categories` directly.
 */
const categories = healthCategories;

export {
  categories,
  categoriesByPrimarySector,
  climateCategories,
  getCategories,
  healthCategories,
};
export type { Category };
